import { Injectable } from "@angular/core";
import clevertap from "clevertap-web-sdk";
import { INSURER_FULL_NAME } from "../constants/common.info";
import { ICleverTapUserInfo } from "../interface/common.interface";

@Injectable({
  providedIn: "root"
})
export class CleverTapService {
  constructor() {}

  // #region User Properties

  // Profile pushes made before onUserLogin land on an anonymous profile, which
  // is only merged into the identified one on the very first login of a device.
  // Buffer them until we have an Identity, then flush.
  private isIdentified = false;
  private pendingUserProperties: { [key: string]: any } = {};

  /**
   * Writes user (profile) properties. Event properties pushed via
   * clevertap.event.push are stored on the event only and never reach the
   * profile, so anything that has to show up under the user must come here.
   * Keys must match the CleverTap property names exactly.
   */
  private updateUserProperties(props: { [key: string]: any }) {
    // The SDK silently drops undefined values, so filter them out rather than
    // letting them look like a write that happened.
    const cleaned: { [key: string]: any } = {};
    Object.keys(props).forEach((key) => {
      if (props[key] !== undefined) {
        cleaned[key] = props[key];
      }
    });

    if (Object.keys(cleaned).length === 0) {
      return;
    }

    if (!this.isIdentified) {
      Object.assign(this.pendingUserProperties, cleaned);
      return;
    }

    clevertap.profile.push({ Site: cleaned });
  }

  private flushPendingUserProperties() {
    if (Object.keys(this.pendingUserProperties).length === 0) {
      return;
    }

    clevertap.profile.push({ Site: this.pendingUserProperties });
    this.pendingUserProperties = {};
  }

  setUserLogin(userInfo: ICleverTapUserInfo) {
    let identityNo = userInfo.identityNo == null ? "NA" : userInfo.identityNo;
    let partnerCode = "D2C";

    let identity =
      identityNo +
      "_" +
      "Car" +
      "_" +
      partnerCode +
      "_" +
      userInfo.vehicleNo +
      "_" +
      userInfo.mobileNo.replace("+", "");

    clevertap.onUserLogin.push({
      Site: {
        // Settings
        "MSG-email": true, // Disable email notifications
        "MSG-push": true, // Enable push notifications
        "MSG-whatsapp": true, // Enable WhatsApp notifications

        // User Info
        Identity: identity,
        CT_PS_ID: identity,
        Email: userInfo.email,
        Name: userInfo.name,
        Phone: userInfo.mobileNo,
        Gender: userInfo.gender,
        IdentityNo: userInfo.identityNo,
        "Marital Status": userInfo.maritalStatus,
        "Preferred Channel": userInfo.preferredChannel,
        Postcode: userInfo.postcode,
        "Vehicle No": userInfo.vehicleNo,
        Language: userInfo.language,
        EhailingUsed: userInfo.eHailingUsed,
        "Type of Insurance": userInfo.insuranceType,
        "Partner Name": partnerCode
      }
    });

    this.isIdentified = true;
    this.flushPendingUserProperties();
  }

  /**
   * Drive+ tier. Pass null when the membership is deselected so the profile is
   * reset instead of keeping a stale tier.
   */
  setDrivePlusMembershipTier(tier: string | null) {
    this.updateUserProperties({
      "Drive+ Membership Tier": tier ? tier : "NA"
    });
  }

  // #endregion

  // #region Event Properties

  //pageViewed(pageIndex: number) {
  //  let currentPage = "";
  //
  //  switch (pageIndex) {
  //    case 0:
  //      currentPage = "Insurers";
  //      break;
  //
  //    case 1:
  //      currentPage = "Add-on";
  //      break;
  //
  //    case 2:
  //      currentPage = "Personal details";
  //      break;
  //
  //    case 3:
  //      currentPage = "Order summary";
  //      break;
  //    default:
  //      break;
  //  }
  //
  //  clevertap.event.push("Page Viewed", {
  //    "URL": window.location.href,
  //    "Page Name": currentPage
  //  });
  //
  //  if (pageIndex == 1) {
  //    clevertap.event.push("Quote Generated", {
  //      "Quote Url": window.location.href
  //    });
  //  }
  //}

  pageViewed() {
    clevertap.event.push("Page Viewed", {
      URL: window.location.href,
      "Page Name": "Insurers"
    });
  }

  quoteGenerated() {
    clevertap.event.push("Quote Generated", {
      "Quote Url": window.location.href
    });
  }

  quotationDetails(
    isEv: boolean,
    ncd: any,
    roadtax: any,
    discountedRoadtax: any
  ) {
    this.updateUserProperties({
      "EV Vehicle": !!isEv,
      NCD: ncd == null ? 0 : ncd,
      "Road Tax": roadtax == null ? 0 : roadtax,
      "Discounted Road Tax": discountedRoadtax == null ? 0 : discountedRoadtax
    });
  }

  lowestBasePremium(bp: any, lqi: string, mi: any) {
    this.updateUserProperties({
      "Lowest Base Premium": bp == null ? 0 : bp,
      "Lowest Quotation Insurer": lqi ? lqi : "NA",
      "Monthly Instalment": mi == null ? 0 : mi
    });
  }

  drivePlusSelected(membership: string) {
    clevertap.event.push("Drive+ Membership Tier Selected", {
      "Drive+ Membership Tier": membership
    });
  }

  insurerSelected(quotationCode: string) {
    clevertap.event.push("Insurer Selected", {
      "Insurer Name": INSURER_FULL_NAME[quotationCode]
    });
  }

  addOnSelected(addOnCode: string) {
    clevertap.event.push("Add-on Selected", {
      "Add-on Name": addOnCode
    });
  }

  motorMembershipSelected(addOnCode: string) {
    clevertap.event.push("Motor Membership Selected", {
      "Motor Membership Name": addOnCode
    });
  }

  personalDetailsFilled(city: string, state: string) {
    clevertap.event.push("Personal Details Filled", {
      City: city,
      State: state
    });
  }

  excessFeeSelected(excessName: string) {
    clevertap.event.push("Excess Fee Selected", {
      "Excess Type": excessName
    });
  }

  promoCodeApplied(name: any) {
    clevertap.event.push("Promo Code Applied", {
      "Promo Code Name": name
    });
  }

  cashCodeApplied(name: any) {
    clevertap.event.push("Cash Code Applied", {
      "Cash Code Name": name
    });
  }

  referralCodeApplied(referralCode: string) {
    clevertap.event.push("Referral Code Applied", {
      "Referral Code": referralCode
    });
  }

  paymentOptionSelected(paymentType: any, bank: any, installmentMonth: any) {
    clevertap.event.push("Payment Option Selected", {
      "Payment Option Type": paymentType,
      "Bank Name": bank,
      "Monthly Option": installmentMonth
    });
  }

  paymentInitiated() {
    clevertap.event.push("Payment Initiated");
  }

  // #endregion
}
